import React, { useEffect, useMemo, useState } from 'react';
import { useMobileOptimizedStyles } from '../../hooks/mobile-hooks';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import type { SitePlanBlock, SitePlanBounds, StackUnitInfo } from '../../types';
import {
  fetchPublicStackAnalysis,
  type PublicStackAnalysisPayload,
} from '../../services/superPickerPublicApi';
import type { PublicUnitForMenu } from './unit/types';
import StackAnalysisDialog from './siteplan/StackAnalysisDialog';
import ProjectLandLotMap from './map/ProjectLandLotMap';

interface SitePlanSectionDetailProps {
  projectName: string;
  imageUrl?: string | null;
  blocks?: SitePlanBlock[];
  bounds?: SitePlanBounds | null;
  /** Per-stack metadata (bedroom / unit type) used to populate the filters. */
  stackInfos?: StackUnitInfo[];
  /** Per-stack sold percentage map (0-100) from the public heat-map endpoint. */
  stackSoldPercentages?: Record<string, number> | null;
  /**
   * Stack identifiers whose dot should pulse to draw attention. Mirrors the
   * agent's `sitePlanBlinkingStacks` set used in `TopViewingSitePlanDetail.tsx`
   * (around line 4812) — applied as the `sitePlanBlink` keyframe animation on
   * the dot `::before` pseudo-element.
   */
  blinkingStacks?: Set<string> | null;
  /**
   * Stack selection coming from the sibling All Blocks Tower View's Stack
   * dropdown (`TowerViewSection`). Values are the raw dropdown selections:
   * numeric stack ids as strings (e.g. `"3"`) plus the sentinels `'ODD'` /
   * `'EVEN'`. When non-empty, the site-plan dots are BOTH filtered (only
   * matching stacks stay visible) AND pulsed — mirroring the agent-side
   * `sitePlanStackFilter` + `sitePlanBlinkingStacks` behaviour in
   * `TopViewingSitePlanDetail.tsx`. `[]` / omitted = no stack filter.
   */
  stackFilter?: string[];
  mapLatitude?: number | null;
  mapLongitude?: number | null;
  /**
   * Optional controlled bedroom filter. When supplied (with
   * `onBedroomFilterChange`), the component renders the value the
   * parent owns instead of its private state — so the same filter
   * can drive a sibling component (e.g. `TowerViewSection`) without
   * duplicating the dropdown. Both props must be provided together.
   */
  bedroomFilter?: number[];
  onBedroomFilterChange?: (next: number[]) => void;
  /**
   * Optional controlled unit-type filter. Same contract as
   * `bedroomFilter` / `onBedroomFilterChange`.
   */
  unitTypeFilter?: string[];
  onUnitTypeFilterChange?: (next: string[]) => void;
  /**
   * Expanded set of unit-type CODES that map to the selected
   * descriptions in `unitTypeFilter`. Mirrors the agent-side
   * `sitePlanUnitTypeFilterExpandedUnitTypes` — a stack matches the
   * filter when either its description is in `unitTypeFilter` OR its
   * code is in this set. Required because the upstream tower-view
   * payload sometimes carries the code without the description.
   * Optional / defaults to an empty set so legacy callers keep
   * working.
   */
  unitTypeFilterCodes?: Set<string>;
  /**
   * Optional project unit-type catalogue from the public
   * `/projects/:projectId/unit-types` endpoint. When supplied, the
   * Unit Type dropdown lists every description from the catalogue
   * (matching the agent's `sitePlanUnitTypes`-driven dropdown) instead
   * of just the descriptions present in `stackInfos`. We only depend
   * on the `description` and `unitTypes` fields here — the rest of
   * `PublicProjectUnitType` is forwarded for future extensions.
   */
  projectUnitTypes?: Array<{
    description: string;
    unitType: string | null;
    unitTypes: string[];
    bedroom: number | null;
  }>;
  /** Used by the stack-analysis dialog when opened (site-plan dots do not open it). */
  projectId?: string | null;
  /**
   * Fired when a unit row inside the stack-analysis dialog is clicked.
   * Parent typically opens the unit-actions menu at the cursor.
   */
  onUnitClick?: (unit: PublicUnitForMenu, mouseX: number, mouseY: number) => void;
}

const ANY_FILTER_VALUE = -999 as const;
/**
 * Sentinel bedroom value reserved for penthouse units in the Bedroom filter.
 * Mirrors `PENTHOUSE_FILTER_VALUE` in the agent-side `TopViewingSitePlanDetail`
 * — penthouses aren't a true bedroom count, so they get a distinct numeric
 * marker (-1) that's safe to coexist with real bedroom counts (1, 2, 3 …).
 */
const PENTHOUSE_FILTER_VALUE = -1;
const SITE_PLAN_MIN_SCALE = 1;
const DOT_SIZE = 32;
const DOT_BORDER = '2px solid rgba(255, 255, 255, 0.85)';
const DOT_SHADOW = '0 2px 6px rgba(0, 0, 0, 0.45)';
const DOT_FONT_SIZE = '0.75rem';

const normalizeStackKey = (stack: string | number | null | undefined): string => {
  if (stack == null) return '';
  return String(stack).trim();
};

/**
 * Display-only rewrite for the Unit Type filter: upstream data labels
 * some unit types as "Premier", but the public-facing copy calls them
 * "PREMIUM" (all caps). The cell labels and the underlying filter
 * values (used for matching against `info.unitType`) intentionally
 * stay untouched — this rewrite is applied only to what the user
 * reads in the dropdown / chip.
 */
const formatUnitTypeLabel = (raw: string): string =>
  raw.replace(/premier/gi, 'PREMIUM');

export const SitePlanSectionDetail: React.FC<SitePlanSectionDetailProps> = ({
  projectName,
  imageUrl,
  blocks = [],
  bounds = null,
  stackInfos = [],
  stackSoldPercentages: stackSoldPercentagesProp = null,
  blinkingStacks = null,
  stackFilter = [],
  mapLatitude = null,
  mapLongitude = null,
  bedroomFilter: bedroomFilterProp,
  onBedroomFilterChange,
  unitTypeFilter: unitTypeFilterProp,
  onUnitTypeFilterChange,
  unitTypeFilterCodes,
  projectUnitTypes = [],
  projectId,
  onUnitClick,
}) => {
  const mobileOptimizedStyles = useMobileOptimizedStyles();
  const [showPoints, setShowPoints] = useState(true);
  const [stackAnalysisOpen, setStackAnalysisOpen] = useState(false);
  const [stackAnalysisLoading, setStackAnalysisLoading] = useState(false);
  const [stackAnalysisData, setStackAnalysisData] = useState<PublicStackAnalysisPayload | null>(null);
  // Stack analysis from site-plan dots is disabled on the public side; dialog kept for future use.
  const canOpenStackAnalysis = false;

  const handleOpenStackAnalysis = async (stackNumber: string, blockNumber?: string | null) => {
    if (!projectId || !stackNumber) return;
    const normalizedStack = stackNumber.trim();
    const stackCandidates = [normalizedStack];
    if (/^\d+$/.test(normalizedStack)) {
      stackCandidates.push(normalizedStack.padStart(2, '0'));
      stackCandidates.push(String(Number(normalizedStack)));
    }

    setStackAnalysisOpen(true);
    setStackAnalysisLoading(true);
    setStackAnalysisData(null);
    try {
      let analysis: PublicStackAnalysisPayload | null = null;
      for (const candidate of Array.from(new Set(stackCandidates.filter(Boolean)))) {
        try {
          analysis = await fetchPublicStackAnalysis(projectId, candidate, blockNumber ?? undefined);
          if (analysis) break;
        } catch (err) {
          const message = err instanceof Error ? err.message.toLowerCase() : '';
          if (!message.includes('404') && !message.includes('no units')) throw err;
        }
      }
      setStackAnalysisData(analysis);
      if (!analysis) setStackAnalysisOpen(false);
    } catch (err) {
      console.error('[SitePlanSectionDetail] stack analysis failed:', err);
      setStackAnalysisOpen(false);
    } finally {
      setStackAnalysisLoading(false);
    }
  };

  const handleCloseStackAnalysis = () => {
    setStackAnalysisOpen(false);
    setStackAnalysisData(null);
  };
  // Controlled / uncontrolled bedroom filter. When the parent passes
  // both `bedroomFilter` + `onBedroomFilterChange` we render their
  // value; otherwise we own a local copy so legacy callers keep
  // working unchanged.
  const [internalBedroomFilter, setInternalBedroomFilter] = useState<number[]>([]);
  const bedroomFilter = bedroomFilterProp ?? internalBedroomFilter;
  const setBedroomFilter = (next: number[]): void => {
    if (onBedroomFilterChange) onBedroomFilterChange(next);
    else setInternalBedroomFilter(next);
  };
  const [internalUnitTypeFilter, setInternalUnitTypeFilter] = useState<string[]>([]);
  const unitTypeFilter = unitTypeFilterProp ?? internalUnitTypeFilter;
  const setUnitTypeFilter = (next: string[]): void => {
    if (onUnitTypeFilterChange) onUnitTypeFilterChange(next);
    else setInternalUnitTypeFilter(next);
  };

  // Per-stack bedroom + unit type look-up (first entry found per stack).
  // We also remember `isPenthouse` so the PH option in the Bedroom filter
  // can match stacks whose unit-type/description reads as a penthouse,
  // and `unitTypeCode` so the Unit Type filter can match against the
  // expanded code-set when the description is unavailable.
  const stackMeta = useMemo(() => {
    const map = new Map<
      string,
      {
        bedroom: number;
        unitType: string;
        unitTypeCode: string;
        isPenthouse: boolean;
      }
    >();
    for (const info of stackInfos) {
      const key = normalizeStackKey(info.stack);
      if (!key || map.has(key)) continue;
      map.set(key, {
        bedroom: info.bedroom,
        unitType: info.unitType,
        unitTypeCode: (info.unitTypeCode || '').trim(),
        isPenthouse: !!info.isPenthouse,
      });
    }
    return map;
  }, [stackInfos]);

  /**
   * Match a normalized stack key against the Tower View Stack selection.
   * Mirrors the agent's `matchesSelectedStackFilter` in
   * `TopViewingSitePlanDetail.tsx`: `'ODD'` / `'EVEN'` match all odd / even
   * numeric stacks; any other value matches by normalized stack id. Returns
   * `true` when no stack filter is active.
   */
  const matchesStackFilter = (stack: string): boolean => {
    if (stackFilter.length === 0) return true;
    const stackNum = Number(stack);
    return stackFilter.some((sel) => {
      const normalized = String(sel).trim().toUpperCase();
      if (normalized === 'ODD') return Number.isFinite(stackNum) && stackNum % 2 === 1;
      if (normalized === 'EVEN') return Number.isFinite(stackNum) && stackNum % 2 === 0;
      return normalizeStackKey(sel) === stack;
    });
  };

  // Numeric bedroom counts present in the tower, plus the PH sentinel when
  // any stack contains a penthouse — mirrors `sitePlanBedroomFilterOptions`
  // on the agent side. PH is appended after the numeric values so the menu
  // reads `1 Bedroom, 2 Bedroom, …, Penthouse`. When Stack and/or Unit Type
  // filters are active, only stacks that pass them contribute options.
  const bedroomOptions = useMemo(() => {
    const set = new Set<number>();
    let hasPenthouse = false;
    const hasUnitTypeFilter = unitTypeFilter.length > 0;
    stackInfos.forEach((info) => {
      if (!matchesStackFilter(info.stack)) return;
      if (hasUnitTypeFilter) {
        const matchesDesc = !!info.unitType && unitTypeFilter.includes(info.unitType);
        const matchesCode =
          !!info.unitTypeCode
          && !!unitTypeFilterCodes
          && unitTypeFilterCodes.has(info.unitTypeCode);
        if (!matchesDesc && !matchesCode) return;
      }
      if (info.isPenthouse) hasPenthouse = true;
      if (Number.isFinite(info.bedroom) && info.bedroom > 0) set.add(info.bedroom);
    });
    const sorted = Array.from(set).sort((a, b) => a - b);
    if (hasPenthouse) sorted.push(PENTHOUSE_FILTER_VALUE);
    return sorted;
  }, [stackInfos, stackFilter, unitTypeFilter, unitTypeFilterCodes]);

  // Prune bedroom selections that are no longer reachable under the active
  // stack / unit-type filters (agent parity with the bedroom-options prune in
  // `SuperPickerApp.tsx`).
  useEffect(() => {
    if (bedroomFilter.length === 0) return;
    const allowed = new Set(bedroomOptions);
    const next = bedroomFilter.filter((v) => allowed.has(v));
    if (next.length !== bedroomFilter.length) {
      setBedroomFilter(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bedroomOptions, bedroomFilter]);

  // Unit-type options shrink to match the active Bedroom + Stack filters —
  // mirrors the agent-side `sitePlanUnitTypesForBedroomFilter` memo in
  // `SuperPickerApp.tsx` (~9654-9680). When the parent has supplied the
  // public `projectUnitTypes` catalogue we drive the dropdown from it
  // (one entry per `description`, agent parity). For each row we resolve
  // an effective bedroom using a three-step fallback that mirrors the
  // agent:
  //   1. live stacks whose unitType code OR description matches the row
  //      (the agent uses `towerViewUnits.projectUnitTypeId`; we don't
  //      expose ids so we match by description/code instead);
  //   2. the catalogue's stored `bedroom` field;
  //   3. parse the leading digit out of the description (e.g.
  //      `"3 Bedroom Premier"` → 3) — agent's `normalizeBedroomValue`.
  // When no catalogue is supplied we fall back to the `stackInfos`-derived
  // set so legacy callers without the API payload keep working.
  const unitTypeOptions = useMemo(() => {
    // PH detection. Agent's `isPenthouseUnit` / `isPenthouseText`.
    const hit = (s: string | null | undefined): boolean => {
      const raw = (s ?? '').trim().toLowerCase();
      if (!raw) return false;
      return (
        raw.includes('penthouse')
        || raw.includes('penthhouse')
        || /\bph\b/.test(raw)
        || raw.endsWith('ph')
      );
    };
    // Extract a numeric bedroom from any string-ish input. Mirrors the
    // agent's `normalizeBedroomValue` for strings like
    // `"3 Bedroom Premier"` / `"3BR"` / `"3"`. Returns null when no
    // leading number is found.
    const parseBedroom = (val: unknown): number | null => {
      if (val == null) return null;
      if (typeof val === 'number') return Number.isFinite(val) ? val : null;
      const str = String(val).trim();
      if (!str) return null;
      const asNum = Number(str);
      if (Number.isFinite(asNum)) return asNum;
      const m = str.match(/(\d+)/);
      return m ? Number(m[1]) : null;
    };

    const stackInfosInScope = stackInfos.filter((info) => matchesStackFilter(info.stack));
    const hasStackFilter = stackFilter.length > 0;

    if (projectUnitTypes.length > 0) {
      const rowIsPenthouse = (row: {
        description: string;
        unitType: string | null;
        unitTypes: string[];
      }): boolean =>
        hit(row.description) || hit(row.unitType) || row.unitTypes.some(hit);

      // Resolve a row's effective bedroom via the three-step fallback.
      // We pre-scan `stackInfos` once per row instead of nesting loops
      // because `stackInfos` is bounded by stack count, not unit count.
      const rowBedroom = (row: {
        description: string;
        unitType: string | null;
        unitTypes: string[];
        bedroom: number | null;
      }): number | null => {
        // 1. live stacks matching by description or code (most reliable).
        const codes = new Set<string>(row.unitTypes);
        if (row.unitType) codes.add(row.unitType);
        for (const info of stackInfosInScope) {
          const stackMatches =
            (info.unitType && info.unitType === row.description)
            || (info.unitTypeCode && codes.has(info.unitTypeCode));
          if (stackMatches && Number.isFinite(info.bedroom) && info.bedroom > 0) {
            return info.bedroom;
          }
        }
        // 2. catalogue's stored bedroom.
        if (row.bedroom != null && Number.isFinite(row.bedroom) && row.bedroom > 0) {
          return row.bedroom;
        }
        // 3. parse from the description text.
        return parseBedroom(row.description);
      };

      const set = new Set<string>();
      projectUnitTypes.forEach((row) => {
        if (!row.description) return;
        if (hasStackFilter) {
          const codes = new Set<string>(row.unitTypes);
          if (row.unitType) codes.add(row.unitType);
          const existsOnSelectedStacks = stackInfosInScope.some(
            (info) =>
              (info.unitType && info.unitType === row.description)
              || (info.unitTypeCode && codes.has(info.unitTypeCode)),
          );
          if (!existsOnSelectedStacks) return;
        }
        if (bedroomFilter.length > 0) {
          const isPH = rowIsPenthouse(row);
          const br = rowBedroom(row);
          const matches = bedroomFilter.some((v) =>
            v === PENTHOUSE_FILTER_VALUE
              ? isPH
              : !isPH && br != null && v === br,
          );
          if (!matches) return;
        }
        set.add(row.description);
      });
      return Array.from(set).sort();
    }
    const set = new Set<string>();
    stackInfosInScope.forEach((info) => {
      if (!info.unitType) return;
      if (bedroomFilter.length > 0) {
        // PH-stacks match when PENTHOUSE_FILTER_VALUE is in the bedroom
        // selection; otherwise we fall back to the literal bedroom match.
        const matches = bedroomFilter.some((v) =>
          v === PENTHOUSE_FILTER_VALUE ? !!info.isPenthouse : v === info.bedroom,
        );
        if (!matches) return;
      }
      set.add(info.unitType);
    });
    return Array.from(set).sort();
  }, [projectUnitTypes, stackInfos, bedroomFilter, stackFilter]);

  // Prune any selected unit-type values that are no longer reachable
  // under the active bedroom filter. Agent parity with the effect at
  // `SuperPickerApp.tsx :: setSitePlanUnitTypeFilter((prev) => prev.filter(...))`.
  // We compare against `unitTypeOptions` (already bedroom-filtered) so
  // the selection follows the visible options exactly.
  useEffect(() => {
    if (unitTypeFilter.length === 0) return;
    const allowed = new Set(unitTypeOptions);
    const next = unitTypeFilter.filter((ut) => allowed.has(ut));
    if (next.length !== unitTypeFilter.length) {
      setUnitTypeFilter(next);
    }
    // `setUnitTypeFilter` is intentionally omitted from deps — it
    // closes over either the parent setter (referentially stable) or
    // our local `useState` setter (also stable). Including it would
    // re-create the closure on every parent render and force the
    // effect to re-run unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitTypeOptions, unitTypeFilter]);

  const hasMapFallback =
    typeof mapLatitude === 'number' &&
    typeof mapLongitude === 'number' &&
    Number.isFinite(mapLatitude) &&
    Number.isFinite(mapLongitude);

  const shouldHideStack = (stack: string): boolean => {
    // Stack filter (from the Tower View Stack dropdown) hides any dot whose
    // stack isn't in the selection — same "filter the dots" behaviour the
    // bedroom / unit-type filters below already apply.
    if (!matchesStackFilter(stack)) return true;
    if (bedroomFilter.length === 0 && unitTypeFilter.length === 0) return false;
    const meta = stackMeta.get(stack);
    if (!meta) return true;
    if (bedroomFilter.length > 0) {
      // Match if ANY selected value applies: PENTHOUSE_FILTER_VALUE matches
      // a stack with at least one penthouse unit; a numeric value matches
      // the stack's representative bedroom count. Mirrors the per-unit
      // `matchesBedroom` logic in `TopViewingSitePlanDetail.tsx`.
      const matches = bedroomFilter.some((v) =>
        v === PENTHOUSE_FILTER_VALUE ? meta.isPenthouse : v === meta.bedroom,
      );
      if (!matches) return true;
    }
    if (unitTypeFilter.length > 0) {
      // Agent parity (`TopViewingSitePlanDetail.tsx :: unitMatchesFilter`):
      // a stack matches when EITHER its description is in the selected
      // descriptions OR its code is in the expanded code-set. Without
      // the code path, units whose tower-view payload omits the
      // description (e.g. PREMIUM units coded as `C5-PREMIUM` without
      // a populated `unitTypeDescription`) would never match.
      const matchesDesc = unitTypeFilter.includes(meta.unitType);
      const matchesCode =
        !!meta.unitTypeCode
        && !!unitTypeFilterCodes
        && unitTypeFilterCodes.has(meta.unitTypeCode);
      if (!matchesDesc && !matchesCode) return true;
    }
    return false;
  };

  return (
    <>
    <Card sx={{ mb: 2, ...mobileOptimizedStyles }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Site Plan
        </Typography>

        {imageUrl ? (
          <Box
            data-pdf-capture="site-map"
            sx={{
              position: 'relative',
              border: '1px solid rgba(var(--color-text-rgb), 0.12)',
              borderRadius: 1,
              overflow: 'hidden',
              height: 'min(600px, 65vh)',
            }}
          >
            <TransformWrapper
              initialScale={1}
              minScale={SITE_PLAN_MIN_SCALE}
              maxScale={4}
              centerOnInit
              limitToBounds
              centerZoomedOut
              disablePadding
              wheel={{ step: 0.05 }}
              pinch={{ step: 5 }}
              doubleClick={{ disabled: false, step: 0.7 }}
              panning={{ disabled: false, velocityDisabled: false }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <TransformComponent
                    wrapperStyle={{
                      width: '100%',
                      height: 'min(600px, 65vh)',
                      overflow: 'hidden',
                    }}
                    contentStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={`${projectName} site plan`}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 'min(600px, 65vh)',
                          width: 'auto',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'contain',
                        }}
                      />
                      {showPoints &&
                        blocks
                          .filter((b) => b.stack)
                          .map((stack, idx) => {
                            const left = bounds
                              ? bounds.x + (stack.x / 100) * bounds.width
                              : stack.x;
                            const top = bounds
                              ? bounds.y + (stack.y / 100) * bounds.height
                              : stack.y;
                            const width = bounds
                              ? (stack.width / 100) * bounds.width
                              : stack.width;
                            const height = bounds
                              ? (stack.height / 100) * bounds.height
                              : stack.height;

                            const stackKey = normalizeStackKey(stack.stack);
                            if (shouldHideStack(stackKey)) return null;

                            // Heatmap removed on the public side: keep a consistent
                            // dot fill color + border style across all stacks.
                            const dotColor = 'var(--color-success)';
                            const labelColor = 'var(--color-white)';
                            const borderColor = DOT_BORDER;

                            // Pulse when explicitly flagged, when a Tower View
                            // stack filter is active, or when bedroom / unit
                            // type filters narrow the visible stacks (agent
                            // parity with `sitePlanBlinkingStacks`).
                            const hasBedroomOrUnitTypeFilter =
                              bedroomFilter.length > 0 || unitTypeFilter.length > 0;
                            const isBlinking =
                              Boolean(blinkingStacks && blinkingStacks.has(stackKey)) ||
                              (stackFilter.length > 0 && matchesStackFilter(stackKey)) ||
                              hasBedroomOrUnitTypeFilter;
                            return (
                              <Box
                                key={`stack-${stack.blockNumber}-${stack.stack}-${idx}`}
                                onClick={() => {
                                  if (!canOpenStackAnalysis || !stack.stack) return;
                                  void handleOpenStackAnalysis(stack.stack, stack.blockNumber);
                                }}
                                sx={{
                                  position: 'absolute',
                                  left: `${left}%`,
                                  top: `${top}%`,
                                  width: `${width}%`,
                                  height: `${height}%`,
                                  cursor: canOpenStackAnalysis ? 'pointer' : 'default',
                                  '@keyframes sitePlanBlink': {
                                    '0%, 100%': {
                                      opacity: 1,
                                      transform: 'translate(-50%, -50%) scale(1)',
                                    },
                                    '50%': {
                                      opacity: 0.3,
                                      transform: 'translate(-50%, -50%) scale(1.3)',
                                    },
                                  },
                                  '&::before': {
                                    content: `"${stack.stack}"`,
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: `${DOT_SIZE}px`,
                                    height: `${DOT_SIZE}px`,
                                    borderRadius: '50%',
                                    backgroundColor: dotColor,
                                    border: borderColor,
                                    boxShadow: DOT_SHADOW,
                                    animation: isBlinking
                                      ? 'sitePlanBlink 0.5s ease-in-out infinite'
                                      : 'none',
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: DOT_FONT_SIZE,
                                    fontWeight: 'bold',
                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                                    color: labelColor,
                                  },
                                }}
                              />
                            );
                          })}
                    </Box>
                  </TransformComponent>

                  {/* Zoom controls (bottom-right, vertical) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      zIndex: 100,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => zoomIn()}
                      sx={{
                        bgcolor: 'var(--color-surface)',
                        border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                        '&:hover': { bgcolor: 'var(--color-surface-hover)' },
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => zoomOut()}
                      sx={{
                        bgcolor: 'var(--color-surface)',
                        border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                        '&:hover': { bgcolor: 'var(--color-surface-hover)' },
                      }}
                    >
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => resetTransform()}
                      sx={{
                        bgcolor: 'var(--color-surface)',
                        border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                        '&:hover': { bgcolor: 'var(--color-surface-hover)' },
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        Reset Zoom
                      </Typography>
                    </IconButton>
                  </Box>
                </>
              )}
            </TransformWrapper>
          </Box>
        ) : hasMapFallback ? (
          <Box>
            <Box
              sx={{
                mb: 1.5,
                py: 1.25,
                px: 1.5,
                borderRadius: 1.5,
                border: '2px solid rgba(var(--color-accent-rgb), 0.55)',
                bgcolor: 'rgba(var(--color-accent-rgb), 0.14)',
                boxShadow: '0 2px 8px rgba(var(--color-shadow-rgb), 0.12)',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--color-accent)',
                }}
              >
                Site Plan Not Available Yet
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'relative',
                border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <ProjectLandLotMap
                latitude={mapLatitude}
                longitude={mapLongitude}
                projectName={projectName}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              minHeight: 320,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(var(--color-text-rgb), 0.12)',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              Site Plan Not Available
            </Typography>
          </Box>
        )}

        {/* Toggles + filters + legend */}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showPoints}
                onChange={(e) => setShowPoints(e.target.checked)}
              />
            }
            label="Show points"
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="public-siteplan-bedroom-filter">Bedroom</InputLabel>
            <Select
              labelId="public-siteplan-bedroom-filter"
              multiple
              value={bedroomFilter.length === 0 ? [ANY_FILTER_VALUE] : bedroomFilter}
              onChange={(e) => {
                const values = e.target.value as Array<string | number>;
                if (values.includes(ANY_FILTER_VALUE)) {
                  const without = values.filter((v) => v !== ANY_FILTER_VALUE);
                  if (bedroomFilter.length > 0) setBedroomFilter([]);
                  else
                    setBedroomFilter(
                      without.map((v) => Number(v)).filter((v) => Number.isFinite(v)),
                    );
                } else {
                  setBedroomFilter(
                    values.map((v) => Number(v)).filter((v) => Number.isFinite(v)),
                  );
                }
              }}
              label="Bedroom"
              renderValue={(selected) => {
                const filtered = (selected as Array<string | number>).filter(
                  (v) => v !== ANY_FILTER_VALUE,
                );
                if (filtered.length === 0) return 'Any';
                return filtered
                  .map((v) => (v === PENTHOUSE_FILTER_VALUE ? 'PH' : `${v}BR`))
                  .join(', ');
              }}
            >
              <MenuItem value={ANY_FILTER_VALUE}>Any</MenuItem>
              {bedroomOptions.map((br) => (
                <MenuItem key={br} value={br}>
                  {br === PENTHOUSE_FILTER_VALUE ? 'Penthouse' : `${br} Bedroom`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="public-siteplan-unittype-filter">Unit Type</InputLabel>
            <Select
              labelId="public-siteplan-unittype-filter"
              multiple
              value={unitTypeFilter.length === 0 ? ['__any__'] : unitTypeFilter}
              onChange={(e) => {
                const values = e.target.value as string[];
                if (values.includes('__any__')) {
                  const without = values.filter((v) => v !== '__any__');
                  if (unitTypeFilter.length > 0) setUnitTypeFilter([]);
                  else setUnitTypeFilter(without);
                } else {
                  setUnitTypeFilter(values);
                }
              }}
              label="Unit Type"
              renderValue={(selected) => {
                const filtered = (selected as string[]).filter((v) => v !== '__any__');
                return filtered.length === 0
                  ? 'Any'
                  : filtered.map(formatUnitTypeLabel).join(', ');
              }}
            >
              <MenuItem value="__any__">Any</MenuItem>
              {unitTypeOptions.map((ut) => (
                // Display-only rewrite: the upstream data labels some
                // unit types as "Premier", but the public-facing copy
                // calls them "Premium". The underlying filter value
                // (and the cell labels) stay untouched so matching
                // against `info.unitType` still works.
                <MenuItem key={ut} value={ut}>
                  {formatUnitTypeLabel(ut)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>
      </CardContent>
    </Card>
    <StackAnalysisDialog
      open={stackAnalysisOpen}
      loading={stackAnalysisLoading}
      data={stackAnalysisData}
      projectId={projectId}
      onClose={handleCloseStackAnalysis}
      onUnitClick={onUnitClick}
    />
    </>
  );
};

export default SitePlanSectionDetail;
